import type { ToolUIPart, UIMessage } from "ai";

import type { AssistantConfig } from "../../contexts/AssistantConfigContext";

const isStaticToolPart = (part: UIMessage["parts"][number]): part is ToolUIPart => part.type.startsWith("tool-");

export type ToolPartDisplay = "pending" | "hidden" | "content";

export type ToolPartView = "none" | "approval" | "confirmed" | "uncertain" | "error" | "output" | "declined";

const ACTION_OUTCOME_UNCERTAIN = "action_outcome_uncertain";

const getOutputError = (output: unknown): unknown =>
  typeof output === "object" && output !== null && "error" in output ? output.error : undefined;

const hasOutputError = (output: unknown): boolean => typeof output === "object" && output !== null && "error" in output;

export const isOutcomeUncertain = (part: ToolUIPart): boolean =>
  (part.state === "output-error" && part.errorText === ACTION_OUTCOME_UNCERTAIN) ||
  (part.state === "output-available" && getOutputError(part.output) === ACTION_OUTCOME_UNCERTAIN);

export const getDefaultToolPartDisplay = (part: ToolUIPart): ToolPartDisplay => {
  if (
    part.state === "input-streaming" ||
    part.state === "input-available" ||
    (part.state === "approval-responded" && part.approval.approved)
  ) {
    return "pending";
  }
  if (part.type === "tool-requestFeedback") {
    return "hidden";
  }
  if (part.state === "output-available" && !hasOutputError(part.output)) {
    return "hidden";
  }
  return "content";
};

export const getToolPartView = (part: ToolUIPart, display: ToolPartDisplay, hasRenderer: boolean): ToolPartView => {
  if (display !== "content" || part.state === "input-streaming" || part.state === "input-available") {
    return "none";
  }
  if (part.state === "approval-requested") {
    return "approval";
  }
  if (part.state === "approval-responded" && part.approval.approved) {
    return "confirmed";
  }
  if (isOutcomeUncertain(part)) {
    return "uncertain";
  }
  if (part.state === "output-available") {
    if (hasOutputError(part.output)) {
      return "error";
    }
    return hasRenderer ? "output" : "none";
  }
  if (part.state === "output-error") {
    return "error";
  }
  return "declined";
};

export const getConfiguredToolPartView = (
  part: ToolUIPart,
  config: Pick<AssistantConfig, "getToolPartDisplay" | "toolRenderers">
): ToolPartView => getToolPartView(part, config.getToolPartDisplay(part), Boolean(config.toolRenderers[part.type]));

export const getProgressStatus = (
  parts: UIMessage["parts"],
  getToolPartDisplay: AssistantConfig["getToolPartDisplay"]
): "thinking" | "lookingUp" =>
  parts.some(
    (part) => isStaticToolPart(part) && part.type !== "tool-requestFeedback" && getToolPartDisplay(part) === "pending"
  )
    ? "lookingUp"
    : "thinking";
